import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  adjustSupplyRequest,
  createSupplyRequest,
  getSuppliesRequest,
  getSupplyHistoryRequest,
  replenishSupplyRequest,
  saveSupplyPricesRequest,
  updateSupplyRequest,
} from './suppliesApi';
import { selectCurrentWarehouseId } from '../warehouseContext/warehouseContextSlice';
import { extractApiError } from '../../utils/errors';

/**
 * Loads the supplies the filters describe, in the warehouse in use — or in
 * every warehouse this session holds when none is picked.
 *
 * @returns {Promise<Array<object>>}
 */
export const fetchSupplies = createAsyncThunk(
  'supplies/fetchSupplies',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { search, onlyActive } = state.supplies.filters;

      const params = {};
      const warehouseId = selectCurrentWarehouseId(state);
      if (warehouseId) params.warehouse = warehouseId;
      if (search) params.search = search;
      if (onlyActive) params.is_active = '1';

      const response = await getSuppliesRequest(params);
      return response.data?.supplies || [];
    } catch (error) {
      console.error('Error fetching supplies:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener los insumos'));
    }
  }
);

/**
 * One supply with its history.
 *
 * There is no endpoint that answers a single supply, so it is looked up in the
 * listing — every supply of every warehouse this session holds, which is a
 * short list by nature — whatever the warehouse in use and the filters are.
 *
 * @param {number|string} supplyId
 * @returns {Promise<{supply: object, movements: Array<object>}>}
 */
export const fetchSupply = createAsyncThunk(
  'supplies/fetchSupply',
  async (supplyId, { rejectWithValue }) => {
    try {
      const { data } = await getSuppliesRequest({});
      const supply = (data?.supplies || []).find((item) => String(item.id) === String(supplyId));
      // Out of reach and non-existent look the same on purpose.
      if (!supply) return rejectWithValue('Insumo no encontrado');

      const history = await getSupplyHistoryRequest(supplyId);
      return { supply, movements: history.data?.movements || [] };
    } catch (error) {
      console.error('Error fetching supply:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo obtener el insumo'));
    }
  }
);

/** Reloads the history of the supply open on its page. */
export const fetchSupplyHistory = createAsyncThunk(
  'supplies/fetchSupplyHistory',
  async (supplyId, { rejectWithValue }) => {
    try {
      const response = await getSupplyHistoryRequest(supplyId);
      return response.data?.movements || [];
    } catch (error) {
      console.error('Error fetching supply history:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo obtener el historial'));
    }
  }
);

/*
 * Every write answers the whole supply, which the slice keeps as the one open,
 * and reloads the listing. The two that move the balance also reload the
 * history: each movement is a new line in it.
 */

/** @param {{warehouseId: number, values: object}} params - See `buildSupplyPayload`. */
export const createSupply = createAsyncThunk(
  'supplies/createSupply',
  async ({ warehouseId, values }, { dispatch, rejectWithValue }) => {
    try {
      const response = await createSupplyRequest({ warehouse_id: warehouseId, ...values });
      dispatch(fetchSupplies());
      return response.data;
    } catch (error) {
      console.error('Error creating supply:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo crear el insumo'));
    }
  }
);

/** @param {{supplyId: number, values: object}} params */
export const updateSupply = createAsyncThunk(
  'supplies/updateSupply',
  async ({ supplyId, values }, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateSupplyRequest(supplyId, values);
      dispatch(fetchSupplies());
      return response.data;
    } catch (error) {
      console.error('Error updating supply:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo guardar el insumo'));
    }
  }
);

/** @param {{supplyId: number, values: object}} params - See `buildReplenishPayload`. */
export const replenishSupply = createAsyncThunk(
  'supplies/replenishSupply',
  async ({ supplyId, values }, { dispatch, rejectWithValue }) => {
    try {
      const response = await replenishSupplyRequest(supplyId, values);
      dispatch(fetchSupplies());
      dispatch(fetchSupplyHistory(supplyId));
      return response.data;
    } catch (error) {
      console.error('Error replenishing supply:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo registrar la reposición'));
    }
  }
);

/** @param {{supplyId: number, values: object}} params - See `buildAdjustPayload`. */
export const adjustSupply = createAsyncThunk(
  'supplies/adjustSupply',
  async ({ supplyId, values }, { dispatch, rejectWithValue }) => {
    try {
      const response = await adjustSupplyRequest(supplyId, values);
      dispatch(fetchSupplies());
      dispatch(fetchSupplyHistory(supplyId));
      return response.data;
    } catch (error) {
      console.error('Error adjusting supply:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo registrar el ajuste'));
    }
  }
);

/** @param {{supplyId: number, prices: object}} params - See `buildPricesPayload`. */
export const saveSupplyPrices = createAsyncThunk(
  'supplies/saveSupplyPrices',
  async ({ supplyId, prices }, { dispatch, rejectWithValue }) => {
    try {
      const response = await saveSupplyPricesRequest(supplyId, prices);
      dispatch(fetchSupplies());
      return response.data;
    } catch (error) {
      console.error('Error saving supply prices:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron guardar los precios'));
    }
  }
);
